import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { RunnerCleanupInstructions } from "@/components/runners/RunnerCleanupInstructions";
import { TrackLocations } from "@/hooks/use-segment";
import { useRunnerEnvironments } from "@/queries/environment-queries";
import { useDeleteRunner, type PlainRunner } from "@/queries/runner-queries";
import { formatError } from "@/utils/errors";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback, type FC } from "react";

type DeleteRunnerModalProps = {
    runner: PlainRunner;
    open: boolean;
    onClose: () => void;
    onDeleteSuccessful?: () => void;
};

export const DeleteRunnerModal: FC<DeleteRunnerModalProps> = ({
    open,
    runner,
    onClose,
    onDeleteSuccessful,
}: DeleteRunnerModalProps) => {
    const { toast } = useToast();

    const deleteRunner = useDeleteRunner();
    const listEnvironments = useRunnerEnvironments(runner.runnerId);

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleDeleteRunner = useCallback(() => {
        deleteRunner.mutate(
            {
                runnerId: runner.runnerId,
                force: false,
            },
            {
                onSuccess: () => {
                    toast({ title: "Runner scheduled for deletion" });
                    onClose();
                    if (onDeleteSuccessful) {
                        onDeleteSuccessful();
                    }
                },
                onError: (err) => {
                    toast({ title: "Failed to delete runner", description: formatError(err) });
                },
            },
        );
    }, [deleteRunner, onClose, onDeleteSuccessful, runner.runnerId, toast]);

    const environments = listEnvironments.data?.environments || [];
    const activeEnvironments = environments.filter((env) => env.status?.phase === EnvironmentPhase.RUNNING);
    const stoppedEnvironments = environments.filter((env) => env.status?.phase !== EnvironmentPhase.RUNNING);
    const totalEnvironments = environments.length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-track-location={TrackLocations.DeleteRunnerModal}>
                <DialogHeader>
                    <DialogTitle>Delete runner</DialogTitle>
                    <DialogDescription>
                        {totalEnvironments > 0 ? (
                            <>
                                <Text>All environments associated with this runner will also be deleted.</Text>
                                <Text className="font-bold">
                                    This will affect {totalEnvironments} environment
                                    <span>{totalEnvironments > 1 ? "s" : ""}</span>:
                                    <ul className="mt-2 list-inside list-disc space-y-1 font-normal">
                                        <li>{activeEnvironments.length} running</li>
                                        <li>{stoppedEnvironments.length} stopped</li>
                                    </ul>
                                </Text>
                            </>
                        ) : (
                            <Text>There are no environments associated with this runner.</Text>
                        )}
                        <Text>Are you sure you&apos;d like to delete it?</Text>
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="py-0">
                    <RunnerCleanupInstructions runner={runner} />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            data-tracking-id="cancel-delete-runner-delete-runner-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        loading={deleteRunner.isPending}
                        onClick={handleDeleteRunner}
                        data-tracking-id="confirm-delete-runner-delete-runner-modal"
                    >
                        Yes, Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const ForceDeleteRunnerModal: FC<DeleteRunnerModalProps> = ({
    open,
    runner,
    onClose,
}: DeleteRunnerModalProps) => {
    const { toast } = useToast();

    const deleteRunner = useDeleteRunner();
    const listEnvironments = useRunnerEnvironments(runner.runnerId);
    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleDeleteRunner = useCallback(() => {
        deleteRunner.mutate(
            { runnerId: runner.runnerId, force: true },
            {
                onSuccess: () => {
                    toast({ title: "Runner force deleted" });
                    onClose();
                },
                onError: (err) => {
                    toast({ title: "Failed to force delete runner", description: formatError(err) });
                },
            },
        );
    }, [deleteRunner, onClose, runner, toast]);

    let withUndeletedText = `${listEnvironments.data?.environments.length} undeleted environments`;
    if (listEnvironments.data?.environments.length === 0) {
        withUndeletedText = "no undeleted environments";
    } else if (listEnvironments.data?.environments.length === 1) {
        withUndeletedText = "1 undeleted environment";
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-track-location={TrackLocations.ForceDeleteRunnerModal}>
                <DialogHeader>
                    <DialogTitle>Warning: Irreversible Action</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody>
                    <Text>
                        You are about to permanently and forcibly delete{" "}
                        <span className="text-lg font-bold text-content-primary">&quot;{runner.name}&quot;</span>{" "}
                        {!listEnvironments.isLoading && (
                            <span>
                                with <strong>{withUndeletedText}</strong>.{" "}
                            </span>
                        )}
                        <span>However, please note:</span>
                    </Text>

                    <ul className="list-outside list-disc py-4 pl-6">
                        <li className="pl-1">
                            <span className="text-lg font-bold">Infrastructure and Files</span>: This action does not
                            guarantee the cleanup of any related infrastructure, files, or other dependencies which may
                            continue to exist.
                        </li>
                        <li className="pl-1">
                            <span className="text-lg font-bold">Manual Cleanup Required</span>: You may need to manually
                            verify and clean up these elements.
                        </li>
                    </ul>

                    <Text>Are you sure you want to proceed with Force Delete?</Text>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            data-tracking-id="cancel-force-delete-runner-delete-runner-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        loading={deleteRunner.isPending}
                        onClick={handleDeleteRunner}
                        data-tracking-id="confirm-force-delete-runner-delete-runner-modal"
                    >
                        Yes, Force Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
